import React, { useEffect, useState } from "react";
import {
	Card,
	Row,
	Col,
	Typography,
	Divider,
	Tag,
	Space,
	Table,
	Badge,
	Descriptions,
	Alert,
	Button,
	message,
} from "antd";
import {
	InfoCircleOutlined,
	CloudServerOutlined,
	CodeOutlined,
	BugOutlined,
	GithubOutlined,
	GlobalOutlined,
	CalendarOutlined,
	TeamOutlined,
	HistoryOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { PiHouseDuotone } from "react-icons/pi";
import { useNavigate } from "react-router";

const { Title, Text, Paragraph } = Typography;

// Import package.json data (you might need to adjust this path)
// For now, we'll define the data directly since we can't import JSON in runtime
import packageInfo from "../../../package.json";

// Changelog data based on git commits with "release:" prefix
// This data structure makes it easy to maintain and update with new releases
const changelogData = [
	{
		version: "0.0.14",
		date: "2025-09-14",
		type: "minor",
		summary: "Server metadata management and API service improvements",
		changes: [
			{
				type: "added",
				description:
					"Server Metadata Editor: Added comprehensive JSON-based metadata editor for server management with validation and proper form handling in both add and edit server pages",
			},
			{
				type: "improved",
				description:
					"API Service Configuration: Updated API service URL configuration for better service integration and connectivity",
			},
			{
				type: "added",
				description:
					"Instance Domain and Metadata Update: Added functionality to update instance domain and metadata with proper validation and confirmation dialogs",
			},
			{
				type: "improved",
				description:
					"Enhanced Instance View Interface: Improved instance detail page with better organization, tabbed layout, and enhanced user experience",
			},
			{
				type: "improved",
				description:
					"Custom Tab Styling: Added no-margin-tabs custom styling for better visual consistency across tabbed interfaces",
			},
			{
				type: "fixed",
				description:
					"Code Quality: Fixed various warnings and improved code quality across components",
			},
		],
	},
	{
		version: "0.0.13",
		date: "2025-09-09",
		type: "minor",
		summary: "Migration to Vite.js, APLA application management, and enhanced instance details",
		changes: [
			{
				type: "changed",
				description:
					"Migrated from Create React App to Vite.js: Complete build system migration for faster development and better performance with modern tooling",
			},
			{
				type: "added",
				description:
					"APLA Application Deployment Management: Added comprehensive deployment list view with status tracking, log viewing, and deployment history for APLA applications",
			},
			{
				type: "added",
				description:
					"APLA Environment Management: Implemented environment management functionality for APLA applications with full CRUD operations and environment configuration",
			},
			{
				type: "added",
				description:
					"Domain Column in Order Items: Added domain information display in order item tables for better order tracking and management",
			},
			{
				type: "improved",
				description:
					"Enhanced Instance Detail View: Updated instance detail page with tabbed interface for better organization and improved user experience",
			},
			{
				type: "improved",
				description:
					"Domain Table Enhancements: Enhanced domain table rows with better data presentation and improved visual hierarchy",
			},
			{
				type: "fixed",
				description:
					"APLA Logs Viewer: Fixed auto-scroll functionality and manual scroll-to-bottom button in the logs viewer for better log monitoring",
			},
			{
				type: "improved",
				description:
					"Custom Tab Styling: Added no-margin-tabs custom styling for better visual consistency across tabbed interfaces",
			},
			{
				type: "changed",
				description:
					"Client Code Authentication: Updated login request header to include client code for improved authentication flow",
			},
		],
	},
	{
		version: "0.0.12",
		date: "2025-08-29",
		type: "minor",
		summary: "Invoice sharing functionality and enhanced domain management",
		changes: [
			{
				type: "added",
				description:
					"Invoice Sharing Feature: Added functionality to share invoice links to the public with secure link generation and clipboard copy functionality",
			},
			{
				type: "improved",
				description:
					"Enhanced Domain Expiration Display: Improved domain table to show exact days until expiration instead of just 'Expiring' status for better visibility",
			},
			{
				type: "improved",
				description:
					"Better Invoice Management: Enhanced invoice view page with share button integration and improved user experience for invoice operations",
			},
		],
	},
	{
		version: "0.0.11",
		date: "2025-08-27",
		type: "minor",
		summary: "WEBUZO panel support and enhanced WEHO account management",
		changes: [
			{
				type: "added",
				description:
					"WEBUZO Panel Support: Added comprehensive support for WEBUZO control panel alongside existing CWP support in server management",
			},
			{
				type: "added",
				description:
					"WEHO Account Detachment: Implemented functionality to detach WEHO accounts from instances with proper API integration",
			},
			{
				type: "changed",
				description:
					"Enhanced Server View: Updated server account tables to intelligently handle both CWP and WEBUZO metadata structures with unified display",
			},
			{
				type: "changed",
				description:
					"Enhanced Instance View: Improved WEHO account display panel to support both CWP (account_info) and WEBUZO (resource) metadata formats",
			},
			{
				type: "changed",
				description:
					"Improved Invoice Display: Enhanced invoice view page with better data presentation and transaction handling",
			},
			{
				type: "changed",
				description:
					"Enhanced Transaction Management: Updated transaction list and view pages with improved data formatting and display",
			},
			{
				type: "fixed",
				description:
					"Application Titles: Standardized page titles across all application pages to 'Shopiva Console' for consistent branding",
			},
			{
				type: "improved",
				description:
					"Resource Usage Display: Enhanced progress bars and resource utilization displays to handle unlimited quotas properly for both panel types",
			},
			{
				type: "improved",
				description:
					"Account Statistics: Improved circular progress indicators for FTP accounts, email accounts, databases, and domain resources",
			},
		],
	},
	{
		version: "0.0.10",
		date: "2025-08-21",
		type: "minor",
		summary: "Customer address management and domain registrar integration",
		changes: [
			{
				type: "added",
				description:
					"Customer Address Management: Full CRUD operations for customer addresses including add, edit, delete functionality with comprehensive form validation",
			},
			{
				type: "added",
				description:
					"Domain Registrar Integration: New DOMA admin service for registering customers to domain registrars (RDASH, SPACESHIP) with address selection",
			},
			{
				type: "added",
				description:
					"Domain Registration Address Tab: Added dedicated tab in customer view for managing domain registration addresses linked to specific registrars",
			},
			{
				type: "changed",
				description:
					"Enhanced Customer View Page: Improved customer detail page with new tabs for addresses and domain registration management",
			},
			{
				type: "changed",
				description:
					"Domain Extension Forms: Updated add and edit forms to use registrar codes (RDASH, SPACESHIP) instead of free-text registrar names for better standardization",
			},
			{
				type: "improved",
				description:
					"Address Card Styling: Enhanced visual presentation of customer address cards with better layout and action buttons",
			},
			{
				type: "improved",
				description:
					"Phone Number Formatting: Added automatic phone number formatting for Indonesian numbers (+62) in address forms",
			},
		],
	},
	{
		version: "0.0.9",
		date: "2025-08-20",
		type: "minor",
		summary: "Enhanced service plan features and APLA service management",
		changes: [
			{
				type: "added",
				description:
					"Service Plan Features Table: Plans now display features in a comprehensive table with code, name, value, and status columns for better feature management",
			},
			{
				type: "added",
				description:
					"APLA Service Table: Added a new table for APLA services in the server view, displaying key metadata fields including UUID, environment ID, status, and timestamps",
			},
			{
				type: "changed",
				description:
					"Unified Plan Feature UI: Both service creation (add.jsx) and editing (edit.jsx) pages now share the same feature management interface for consistency",
			},
			{
				type: "changed",
				description:
					"Enhanced feature data structure: Plans can now have different values for the same feature, allowing for flexible service configuration",
			},
			{
				type: "improved",
				description:
					"Better feature management logic with add/remove/update capabilities and improved data initialization",
			},
		],
	},
	{
		version: "0.0.8",
		date: "2025-08-17",
		type: "patch",
		summary: "Enhanced detail pages and improved user experience",
		changes: [
			{
				type: "changed",
				description:
					"Updated invoice detail page with improved layout and functionality for better invoice management",
			},
			{
				type: "changed",
				description:
					"Enhanced order and invoice items table components with better data presentation and user interaction",
			},
			{
				type: "changed",
				description:
					"Updated cron logs view page with improved logging information display and navigation",
			},
			{
				type: "changed",
				description:
					"Improved domain expiring tag display for better visibility of domain expiration status",
			},
		],
	},
	{
		version: "0.0.7",
		date: "2025-08-17",
		type: "patch",
		summary: "UI improvements and enhanced table functionality",
		changes: [
			{
				type: "changed",
				description:
					"Updated application font family to Geist for better typography and readability",
			},
			{
				type: "changed",
				description: "Enhanced list ordering functionality for improved data organization",
			},
			{
				type: "changed",
				description:
					"Updated order and invoice table components with better user interface and functionality",
			},
		],
	},
	{
		version: "0.0.6",
		date: "2025-08-17",
		type: "minor",
		summary: "Enhanced domain management and system improvements",
		changes: [
			{
				type: "added",
				description:
					"Domain registrar editing functionality allowing admins to update registrar code and name",
			},
			{
				type: "changed",
				description:
					"Enhanced list and view components across the application for better user experience",
			},
			{
				type: "fixed",
				description:
					"Resolved filter parameter separation issue in API calls for better backend compatibility",
			},
			{
				type: "changed",
				description: "Updated instance page titles for improved navigation clarity",
			},
			{
				type: "fixed",
				description: "Fixed invoice item display and calculation issues",
			},
		],
	},
	{
		version: "0.0.5",
		date: "2025-08-16",
		type: "minor",
		summary: "Major feature additions and improvements",
		changes: [
			{
				type: "added",
				description: "Domain billing information update functionality for admin users",
			},
			{
				type: "added",
				description: "Instance billing information update functionality for admin users",
			},
			{
				type: "added",
				description: "Server ping status functionality to monitor server connectivity",
			},
			{
				type: "added",
				description: "Application logs system for better debugging and monitoring",
			},
			{
				type: "added",
				description: "Comprehensive changelog information system with version history",
			},
			{
				type: "changed",
				description: "Enhanced dashboard statistics with more functional date range selection",
			},
			{
				type: "changed",
				description: "Improved server panel options with better user experience",
			},
			{
				type: "changed",
				description: "Updated tables across the application for better usability",
			},
			{
				type: "changed",
				description: "Enhanced service detail page with improved information display",
			},
			{
				type: "fixed",
				description: "Fixed favicon display issues across the application",
			},
		],
	},
	{
		version: "0.0.4",
		date: "2025-08-16",
		type: "patch",
		summary: "Service list improvements and bug fixes",
		changes: [
			{
				type: "changed",
				description:
					"Enhanced service list component with improved user experience and better performance",
			},
			{
				type: "changed",
				description: "Optimized component rendering for better responsiveness",
			},
		],
	},
	{
		version: "0.0.3",
		date: "2025-08-15",
		type: "minor",
		summary: "App information page and pagination fixes",
		changes: [
			{
				type: "added",
				description:
					"Comprehensive app information page with detailed system information, technology stack, and dependency management",
			},
			{
				type: "added",
				description:
					"Real-time system information display including platform details, memory info, and browser capabilities",
			},
			{
				type: "fixed",
				description: "Resolved pagination issues across multiple data grid components",
			},
			{
				type: "fixed",
				description: "Improved data loading performance for large datasets",
			},
		],
	},
	{
		version: "0.0.2",
		date: "2025-08-15",
		type: "patch",
		summary: "Critical pagination fixes",
		changes: [
			{
				type: "fixed",
				description:
					"Fixed pagination functionality for all data lists and tables throughout the application",
			},
			{
				type: "fixed",
				description:
					"Resolved pagination page size configuration issues that were affecting data display",
			},
			{
				type: "fixed",
				description: "Improved pagination controls responsiveness on mobile devices",
			},
		],
	},
	{
		version: "0.0.1",
		date: "2025-08-13",
		type: "major",
		summary: "Initial release of Shopiva Console",
		changes: [
			{
				type: "added",
				description: "Initial release of Shopiva Console application with core functionality",
			},
			{
				type: "added",
				description: "User authentication and session management system",
			},
			{
				type: "added",
				description: "Dashboard with key metrics and navigation",
			},
			{
				type: "added",
				description: "Responsive design optimized for desktop and mobile devices",
			},
			{
				type: "added",
				description: "Integration with Ant Design UI component library",
			},
		],
	},
];

function AppInfoPage() {
	const navigate = useNavigate();
	const [systemInfo, setSystemInfo] = useState({
		userAgent: "",
		platform: "",
		language: "",
		cookieEnabled: false,
		onLine: false,
		memory: null,
		hardwareConcurrency: 0,
		screen: {
			width: 0,
			height: 0,
			colorDepth: 0,
		},
		timezone: "",
		buildTime: "",
	});

	useEffect(() => {
		document.title = "Shopiva Console | App Information";

		// Gather system information
		const info = {
			userAgent: navigator.userAgent,
			platform: navigator.platform,
			language: navigator.language,
			cookieEnabled: navigator.cookieEnabled,
			onLine: navigator.onLine,
			memory: navigator.deviceMemory || null,
			hardwareConcurrency: navigator.hardwareConcurrency,
			screen: {
				width: window.screen.width,
				height: window.screen.height,
				colorDepth: window.screen.colorDepth,
			},
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			buildTime: import.meta.env.VITE_BUILD_TIME || dayjs().format("YYYY-MM-DD HH:mm:ss"),
		};

		setSystemInfo(info);
	}, []);

	// Prepare dependencies data for table
	const dependenciesData = Object.entries(packageInfo.dependencies).map(
		([name, version], index) => ({
			key: index,
			name,
			version,
			category: getCategoryByPackageName(name),
		})
	);

	// Helper function to categorize packages
	function getCategoryByPackageName(name) {
		if (name.includes("react")) return "React";
		if (name.includes("antd") || name.includes("ant-design")) return "UI Framework";
		if (name.includes("testing") || name.includes("jest")) return "Testing";
		if (name.includes("types") || name.includes("typescript")) return "TypeScript";
		if (name === "axios") return "HTTP Client";
		if (name === "dayjs") return "Date/Time";
		if (name === "firebase") return "Firebase";
		if (name.includes("router")) return "Routing";
		if (name.includes("icon")) return "Icons";
		if (name.includes("country") || name.includes("phone")) return "Internationalization";
		if (name.includes("color") || name.includes("image")) return "Media/Graphics";
		return "Other";
	}

	// Helper function to get change type styling
	function getChangeTypeConfig(type) {
		const configs = {
			added: { color: "green", icon: "✨", label: "Added" },
			changed: { color: "blue", icon: "🔄", label: "Changed" },
			fixed: { color: "orange", icon: "🐛", label: "Fixed" },
			removed: { color: "red", icon: "❌", label: "Removed" },
			security: { color: "purple", icon: "🔒", label: "Security" },
			deprecated: { color: "volcano", icon: "⚠️", label: "Deprecated" },
		};
		return configs[type] || configs.changed;
	}

	// Helper function to get version badge color
	function getVersionBadgeColor(type) {
		const colors = {
			major: "red",
			minor: "blue",
			patch: "green",
		};
		return colors[type] || "default";
	}

	const dependencyColumns = [
		{
			title: "Package Name",
			dataIndex: "name",
			key: "name",
			sorter: (a, b) => a.name.localeCompare(b.name),
			render: (text) => <code style={{ fontSize: "12px" }}>{text}</code>,
		},
		{
			title: "Version",
			dataIndex: "version",
			key: "version",
			sorter: (a, b) => a.version.localeCompare(b.version),
			render: (text) => <Tag color="blue">{text}</Tag>,
		},
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
			filters: [
				{ text: "React", value: "React" },
				{ text: "UI Framework", value: "UI Framework" },
				{ text: "Testing", value: "Testing" },
				{ text: "TypeScript", value: "TypeScript" },
				{ text: "HTTP Client", value: "HTTP Client" },
				{ text: "Date/Time", value: "Date/Time" },
				{ text: "Firebase", value: "Firebase" },
				{ text: "Routing", value: "Routing" },
				{ text: "Icons", value: "Icons" },
				{ text: "Internationalization", value: "Internationalization" },
				{ text: "Media/Graphics", value: "Media/Graphics" },
				{ text: "Other", value: "Other" },
			],
			onFilter: (value, record) => record.category === value,
			render: (text) => {
				const colors = {
					React: "volcano",
					"UI Framework": "geekblue",
					Testing: "green",
					TypeScript: "blue",
					"HTTP Client": "purple",
					"Date/Time": "orange",
					Firebase: "gold",
					Routing: "lime",
					Icons: "cyan",
					Internationalization: "magenta",
					"Media/Graphics": "red",
					Other: "default",
				};
				return <Tag color={colors[text] || "default"}>{text}</Tag>;
			},
		},
	];

	const copyToClipboard = () => {
		const appInfo = {
			name: packageInfo.name,
			version: packageInfo.version,
			buildTime: systemInfo.buildTime,
			userAgent: systemInfo.userAgent,
			platform: systemInfo.platform,
			dependencies: packageInfo.dependencies,
		};

		navigator.clipboard
			.writeText(JSON.stringify(appInfo, null, 2))
			.then(() => {
				message.success("App information copied to clipboard!");
			})
			.catch(() => {
				message.error("Failed to copy to clipboard");
			});
	};

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiHouseDuotone />} onClick={() => navigate("/")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						App Information
					</Title>
				</Space>
			</div>

			<Row gutter={[24, 24]}>
				{/* App Overview */}
				<Col xs={24} lg={12}>
					<Card
						title={
							<Space>
								<InfoCircleOutlined />
								Application Overview
							</Space>
						}
						bordered={false}>
						<Descriptions column={1} size="small">
							<Descriptions.Item label="App Name">
								<Text strong>{packageInfo.name}</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Version">
								<Badge count={packageInfo.version} color="blue" />
							</Descriptions.Item>
							<Descriptions.Item label="Homepage">
								<a href={packageInfo.homepage} target="_blank" rel="noopener noreferrer">
									{packageInfo.homepage}
								</a>
							</Descriptions.Item>
							<Descriptions.Item label="Build Time">
								<Text>
									<CalendarOutlined style={{ marginRight: 4 }} />
									{systemInfo.buildTime}
								</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Environment">
								<Tag color={import.meta.env.MODE === "production" ? "green" : "orange"}>
									{import.meta.env.MODE || "development"}
								</Tag>
							</Descriptions.Item>
						</Descriptions>

						<Divider />

						<Space>
							<Button type="primary" icon={<CodeOutlined />} onClick={copyToClipboard}>
								Copy App Info
							</Button>
							<Button
								icon={<BugOutlined />}
								onClick={() =>
									window.open("https://github.com/codeiva/shopiva-console/issues", "_blank")
								}>
								Report Issue
							</Button>
						</Space>
					</Card>
				</Col>

				{/* System Information */}
				<Col xs={24} lg={12}>
					<Card
						title={
							<Space>
								<CloudServerOutlined />
								System Information
							</Space>
						}
						bordered={false}>
						<Descriptions column={1} size="small">
							<Descriptions.Item label="Platform">
								<Text code>{systemInfo.platform}</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Language">
								<Text>{systemInfo.language}</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Timezone">
								<Text>
									<GlobalOutlined style={{ marginRight: 4 }} />
									{systemInfo.timezone}
								</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Screen Resolution">
								<Text>
									{systemInfo.screen.width} × {systemInfo.screen.height}
								</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Color Depth">
								<Text>{systemInfo.screen.colorDepth} bit</Text>
							</Descriptions.Item>
							<Descriptions.Item label="CPU Cores">
								<Badge count={systemInfo.hardwareConcurrency} color="green" />
							</Descriptions.Item>
							{systemInfo.memory && (
								<Descriptions.Item label="Device Memory">
									<Text>{systemInfo.memory} GB</Text>
								</Descriptions.Item>
							)}
							<Descriptions.Item label="Cookies Enabled">
								<Badge
									status={systemInfo.cookieEnabled ? "success" : "error"}
									text={systemInfo.cookieEnabled ? "Yes" : "No"}
								/>
							</Descriptions.Item>
							<Descriptions.Item label="Online Status">
								<Badge
									status={systemInfo.onLine ? "success" : "error"}
									text={systemInfo.onLine ? "Online" : "Offline"}
								/>
							</Descriptions.Item>
						</Descriptions>
					</Card>
				</Col>

				{/* Technology Stack */}
				<Col xs={24}>
					<Card
						title={
							<Space>
								<TeamOutlined />
								Technology Stack Summary
							</Space>
						}
						bordered={false}>
						<Row gutter={[16, 16]}>
							<Col xs={24} sm={12} md={8} lg={6}>
								<Card size="small" bordered>
									<div style={{ textAlign: "center" }}>
										<Text strong>Frontend Framework</Text>
										<br />
										<Badge count="React 18.3.1" color="blue" />
									</div>
								</Card>
							</Col>
							<Col xs={24} sm={12} md={8} lg={6}>
								<Card size="small" bordered>
									<div style={{ textAlign: "center" }}>
										<Text strong>UI Library</Text>
										<br />
										<Badge count="Ant Design 5.23.3" color="geekblue" />
									</div>
								</Card>
							</Col>
							<Col xs={24} sm={12} md={8} lg={6}>
								<Card size="small" bordered>
									<div style={{ textAlign: "center" }}>
										<Text strong>HTTP Client</Text>
										<br />
										<Badge count="Axios 0.26.1" color="purple" />
									</div>
								</Card>
							</Col>
							<Col xs={24} sm={12} md={8} lg={6}>
								<Card size="small" bordered>
									<div style={{ textAlign: "center" }}>
										<Text strong>Build Tool</Text>
										<br />
										<Badge count="Create React App" color="green" />
									</div>
								</Card>
							</Col>
						</Row>
					</Card>
				</Col>

				{/* Release Changelog */}
				<Col xs={24}>
					<Card
						title={
							<Space>
								<HistoryOutlined />
								Release History & Changelog
							</Space>
						}
						bordered={false}>
						<Alert
							message="Release Information"
							description={
								<div>
									<Paragraph style={{ marginBottom: 8 }}>
										This section shows the release history and changes for each version of the
										Shopiva Console application. All releases are tracked from Git commits with
										'release:' prefix for consistent versioning.
									</Paragraph>
									<Space>
										<Text strong>Total Releases:</Text>
										<Badge count={changelogData.length} color="blue" />
										<Text strong>Latest Version:</Text>
										<Tag color="green">v{packageInfo.version}</Tag>
									</Space>
								</div>
							}
							type="info"
							showIcon
							style={{ marginBottom: 16 }}
						/>

						<div style={{ maxHeight: "600px", overflowY: "auto" }}>
							{changelogData.map((release, index) => (
								<Card
									key={release.version}
									size="small"
									style={{
										marginBottom: 16,
										border:
											release.version === packageInfo.version
												? "2px solid #1890ff"
												: "1px solid #d9d9d9",
										boxShadow:
											release.version === packageInfo.version
												? "0 2px 8px rgba(24, 144, 255, 0.15)"
												: "none",
									}}
									title={
										<div>
											<Space>
												<Badge
													count={`v${release.version}`}
													color={getVersionBadgeColor(release.type)}
												/>
												{release.version === packageInfo.version && (
													<Tag color="blue">Current Version</Tag>
												)}
												<Text type="secondary" style={{ fontSize: "12px" }}>
													<CalendarOutlined style={{ marginRight: 4 }} />
													{release.date}
												</Text>
												<Divider type="vertical" />
												{release.summary && (
													<div>
														<Text
															type="secondary"
															style={{ fontSize: "13px", fontStyle: "italic" }}>
															{release.summary}
														</Text>
													</div>
												)}
											</Space>
										</div>
									}>
									<div style={{ paddingLeft: 8 }}>
										{release.changes.map((change, changeIndex) => {
											const config = getChangeTypeConfig(change.type);
											return (
												<div key={changeIndex} style={{ marginBottom: 8 }}>
													<Space align="start">
														<span style={{ fontSize: "16px", marginTop: "2px" }}>
															{config.icon}
														</span>
														<div>
															<Tag color={config.color} size="small" style={{ marginBottom: 4 }}>
																{config.label}
															</Tag>
															<Text style={{ display: "block", lineHeight: "1.4" }}>
																{change.description}
															</Text>
														</div>
													</Space>
												</div>
											);
										})}
									</div>
								</Card>
							))}
						</div>

						<Divider />

						<div style={{ textAlign: "center" }}>
							<Space direction="vertical" size="small">
								<Text type="secondary" style={{ fontSize: "12px" }}>
									<InfoCircleOutlined style={{ marginRight: 4 }} />
									Showing {changelogData.length} releases • Changes tracked from Git commits with
									"release:" prefix
								</Text>
								<Text type="secondary" style={{ fontSize: "11px" }}>
									Latest: v{changelogData[0]?.version} • Total changes:{" "}
									{changelogData.reduce((total, release) => total + release.changes.length, 0)}
								</Text>
							</Space>
						</div>
					</Card>
				</Col>

				{/* Dependencies */}
				<Col xs={24}>
					<Card
						title={
							<Space>
								<GithubOutlined />
								Dependencies ({Object.keys(packageInfo.dependencies).length} packages)
							</Space>
						}
						bordered={false}>
						<Alert
							message="Package Information"
							description="This table shows all the packages and libraries used in this application. You can filter by category or search for specific packages."
							type="info"
							showIcon
							style={{ marginBottom: 16 }}
						/>
						<Table
							columns={dependencyColumns}
							dataSource={dependenciesData}
							size="small"
							pagination={{
								pageSize: 10,
								showSizeChanger: true,
								showQuickJumper: true,
								showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} packages`,
							}}
							scroll={{ x: true }}
							bordered
						/>
					</Card>
				</Col>
			</Row>
		</div>
	);
}

export default AppInfoPage;
